import '../styles/v2/pages/onwatch.css';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import fetchWithTenant from '../utils/fetchWithTenant';
import InfoTooltip from '../components/InfoTooltip';
import {
  ArrowLeft, Loader2, FileArchive, Radio, Video,
} from 'lucide-react';

const POLL_INTERVAL = 15000;

const STATUS_CLASS = { CONNECTED: 'healthy', RECONNECTING: 'warning', DISCONNECTED: 'critical', UNKNOWN: 'unknown' };
const POINT_HEALTH_CLASS = { HEALTHY: 'healthy', UNHEALTHY: 'critical', DISABLED: 'informational', UNKNOWN: 'unknown' };
const UPTIME_WINDOW_LABEL = { '24h': '24 hours', '7d': '7 days', '30d': '30 days' };

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function formatDuration(seconds) {
  if (seconds == null) return 'Unknown';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function shortenServiceAddress(addr) {
  if (!addr) return null;
  return addr.split('.')[0];
}

// Prompt 2j-fix, Task E: unackAlertCount is only meaningful relative to
// the rest of the instance - 6 versus 5267 says nothing on its own.
function formatUnackContext(count, stats) {
  if (count == null || !stats || stats.deviceCount === 0) return null;
  if (stats.average == null) return null;
  const avg = Math.round(stats.average);
  if (count === stats.max && count > avg) return `highest on this instance (average ${avg})`;
  if (avg === 0) return `instance average ${avg}`;
  const ratio = count / avg;
  if (ratio >= 1.5) return `${ratio.toFixed(1)}x the instance average (${avg})`;
  if (ratio <= 0.67) return `below the instance average (${avg})`;
  return `near the instance average (${avg})`;
}

// Phase 2g, Task E: "current state for 2h 33m" begs the question, since
// what - pair the relative duration with the absolute clock time.
function formatClockTime(dateStr) {
  return new Date(dateStr).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const GAP_NEGLIGIBLE_SECONDS = 60;

/**
 * Prompt 2g-fix, Item 1: the pre-monitoring remainder ("29d 18h not
 * monitored" on a 30 day card for a 7 hour old instance) is dropped
 * entirely - true and useless. Each card states only its own observed
 * span; a genuine in-period gap (gapSeconds, computed server-side in
 * onwatch.history.cjs) is shown separately, never blended into this text.
 */
function formatCardUptime(uptime) {
  if (uptime.uptimeFraction == null) return { pct: null, text: 'no monitored time in this window' };
  const pct = (uptime.uptimeFraction * 100).toFixed(1);
  return { pct, text: `${pct}% uptime, ${formatDuration(uptime.coverageSeconds)} observed` };
}

/**
 * The uptime trap (B2's own framing): a naive percentage over a window
 * where collection was half blind reads as false confidence either way.
 * Coverage is always shown alongside the percentage, never the number
 * alone, and a fully-uncovered window says so rather than showing 0% or
 * 100%.
 */
function UptimeCard({ windowKey, uptime, gapText, instanceUptimeBaseline }) {
  const { pct, text } = formatCardUptime(uptime);
  // Task D: instance-average baseline is only computed for the 24h window
  // (see getInstanceUptimeBaseline) - the window most naturally compared
  // against a cross-camera average.
  const showBaseline = windowKey === '24h' && instanceUptimeBaseline && !instanceUptimeBaseline.insufficientHistory
    && uptime.uptimeFraction != null;
  const baselineDeltaPts = showBaseline
    ? Math.round((uptime.uptimeFraction - instanceUptimeBaseline.averageUptimeFraction) * 1000) / 10
    : null;

  return (
    <div className="ow-uptime-card">
      <div className="ow-metric-label">{UPTIME_WINDOW_LABEL[windowKey]}</div>
      <div className="ow-metric-value" style={{ fontSize: 20 }}>{pct != null ? `${pct}%` : 'Unknown'}</div>
      <div className="ow-form-help">{text}</div>
      {/* Only rendered here when this window's gap differs from the
          others - see the section-level combined line below the grid
          for the common case where all windows agree. */}
      {gapText && <div className="ow-form-help ow-coverage-gap-text">{gapText}</div>}
      {showBaseline && (
        <div className="ow-form-help">
          instance average {(instanceUptimeBaseline.averageUptimeFraction * 100).toFixed(1)}%
          {Math.abs(baselineDeltaPts) >= 0.1 ? ` (${baselineDeltaPts > 0 ? '+' : ''}${baselineDeltaPts}pt)` : ' (typical)'}
        </div>
      )}
      {windowKey === '24h' && instanceUptimeBaseline?.insufficientHistory && (
        <div className="ow-form-help">baseline building</div>
      )}
      <div className="ow-form-help">{uptime.disconnects} disconnect{uptime.disconnects === 1 ? '' : 's'}</div>
      {uptime.longestOutageSeconds > 0 && (
        <div className="ow-form-help">Longest outage: {formatDuration(uptime.longestOutageSeconds)}</div>
      )}
    </div>
  );
}

/**
 * Prompt 2g-fix, Item 1: "Compute per window if the gap falls differently
 * within each; otherwise show once." A young instance's gap is
 * mathematically identical across every window wider than its own age
 * (see computeGapSeconds's comment in onwatch.history.cjs), so the common
 * case is one combined line below the three cards. A mature instance can
 * genuinely have different gaps per window (a recent outage inside 24h
 * that a 30-day rollup absorbs differently), in which case each card
 * shows its own. No gap anywhere renders nothing - the normal case.
 */
function UptimeCardsSection({ uptimeByWindow, instanceRollupFirstSeenAt, instanceUptimeBaseline }) {
  const windowKeys = Object.keys(uptimeByWindow);
  const gaps = windowKeys.map((k) => uptimeByWindow[k].gapSeconds || 0);
  const anyGap = gaps.some((g) => g > GAP_NEGLIGIBLE_SECONDS);
  const uniform = anyGap && gaps.every((g) => Math.abs(g - gaps[0]) <= GAP_NEGLIGIBLE_SECONDS);

  const beganAgoLabel = instanceRollupFirstSeenAt
    ? formatDuration((Date.now() - new Date(instanceRollupFirstSeenAt).getTime()) / 1000)
    : null;

  return (
    <>
      <div className="ow-uptime-grid">
        {windowKeys.map((windowKey) => (
          <UptimeCard
            key={windowKey}
            windowKey={windowKey}
            uptime={uptimeByWindow[windowKey]}
            instanceUptimeBaseline={instanceUptimeBaseline}
            gapText={
              anyGap && !uniform && uptimeByWindow[windowKey].gapSeconds > GAP_NEGLIGIBLE_SECONDS
                ? `${formatDuration(uptimeByWindow[windowKey].gapSeconds)} gap in coverage since monitoring began.`
                : null
            }
          />
        ))}
      </div>
      {beganAgoLabel && <div className="ow-form-help" style={{ marginTop: 8 }}>Monitoring began {beganAgoLabel} ago.</div>}
      {anyGap && uniform && (
        <div className="ow-form-help ow-coverage-gap-text" style={{ marginTop: 2 }}>
          {formatDuration(gaps[0])} gap in coverage since monitoring began.
        </div>
      )}
    </>
  );
}

export default function OnWatchCameraDetail() {
  const { id, cameraId } = useParams();
  const navigate = useNavigate();

  const [instance, setInstance] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [instRes, detailRes] = await Promise.all([
        fetchWithTenant(`/api/onwatch/instances/${id}`),
        fetchWithTenant(`/api/onwatch/instances/${id}/cameras/${encodeURIComponent(cameraId)}/detail`),
      ]);
      if (!instRes.ok) throw new Error(`HTTP ${instRes.status}`);
      if (!detailRes.ok) throw new Error(`HTTP ${detailRes.status}`);
      setInstance((await instRes.json()).instance);
      setDetail(await detailRes.json());
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [id, cameraId]);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(() => fetchAll(true), POLL_INTERVAL);
    return () => clearInterval(timer);
  }, [fetchAll]);

  if (loading && !detail) {
    return (
      <div className="page-content">
        <div className="md-loading"><Loader2 size={32} className="spin" /><p>Loading camera detail...</p></div>
      </div>
    );
  }

  if (error && !detail) {
    return (
      <div className="page-content">
        <div className="ow-form-error">{error}</div>
        <Link to={`/monitoring/onwatch/${id}/cameras`}><ArrowLeft size={14} /> Back to cameras</Link>
      </div>
    );
  }

  const { camera, metrics, unackAlertCount, unackAlertStats, disconnectHistory, instanceRollupFirstSeenAt } = detail;

  // Same per-tier staleness treatment as every other OnWatch page: the app
  // tier can be stale independently of everything else, and this page
  // must never show a live status color while that is true.
  const appConfigured = !!(instance?.appLastSuccessAt || instance?.appLastError);
  const appStale = appConfigured && (
    !instance.appLastSuccessAt ||
    (Date.now() - new Date(instance.appLastSuccessAt).getTime()) / 1000 > (instance.stalenessSec || 300)
  );

  const meta = camera.metadata || {};
  const pointHealth = camera.isPoint ? (meta.pointHealth || 'UNKNOWN') : null;

  return (
    <div className="page-content">
      <Link to={`/monitoring/onwatch/${id}/cameras`} style={{ fontSize: 12, color: 'var(--text-tertiary)', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        <ArrowLeft size={12} /> Cameras
      </Link>

      <div className="page-header-with-actions">
        <div>
          <h1 className="page-title">
            {camera.isPoint ? <Radio size={22} /> : <Video size={22} />} {camera.title || camera.sourceId}
          </h1>
          <div className="ow-workload-detail-meta">
            {camera.isPoint ? 'Point' : 'Camera'}, streamType {camera.streamType}
            {' · '}current state for {formatDuration(camera.currentStateDurationSeconds)}
            {camera.lastStatusChange && ` (since ${formatClockTime(camera.lastStatusChange)})`}
            {appStale && ' · stale (app tier not current)'}
          </div>
        </div>
        <div className="ow-controls">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => navigate(`/monitoring/onwatch/${id}?collectLogs=${encodeURIComponent('cv-engine,camera-service')}`)}
          >
            <FileArchive size={14} /> Collect logs
          </button>
        </div>
      </div>

      {appStale && (
        <div className="ow-wizard-stale-note" style={{ marginBottom: 14 }}>
          The app tier has not reported successfully within the staleness window. Statuses below are shown
          gray, not live, as of {timeAgo(instance.appLastSuccessAt)}.
        </div>
      )}

      <div className="ow-node-card">
        <div className="ow-node-header">
          <span className="ow-node-name">Status</span>
        </div>
        {camera.isPoint ? (
          <>
            <div className="ow-metric-row">
              <div className="ow-metric-label-row">
                <span className="ow-metric-label">Point health</span>
                <span className={`ow-badge ${appStale ? 'unknown' : (POINT_HEALTH_CLASS[pointHealth] || 'unknown')}`}>
                  {appStale ? 'Unknown' : pointHealth}
                </span>
              </div>
            </div>
            <div className="ow-metric-row">
              <div className="ow-metric-label-row">
                <span className="ow-metric-label">
                  Socket connected
                  <InfoTooltip text="Points connect by socket rather than RTSP video, so their stream status is not a health indicator. Socket state is what matters." />
                </span>
                <span className="ow-metric-value">{meta.isSocketConnected == null ? '—' : (meta.isSocketConnected ? 'Yes' : 'No')}</span>
              </div>
            </div>
            <div className="ow-metric-row">
              <div className="ow-metric-label-row">
                <span className="ow-metric-label">
                  Sync state
                  <InfoTooltip text="When this Point last synchronized its local watchlist. A stale sync means it recognizes people against outdated data, so a revoked person could still be admitted." />
                </span>
                <span className="ow-metric-value">{meta.syncState || '—'}{meta.lastSuccessfulSync ? ` (${timeAgo(meta.lastSuccessfulSync)})` : ''}</span>
              </div>
            </div>
            {meta.accessoryName && (
              <div className="ow-metric-row">
                <div className="ow-metric-label-row">
                  <span className="ow-metric-label">
                    Accessory
                    <InfoTooltip text="The physical relay or controller this Point triggers. If it is offline, recognition can succeed while the door does not open." />
                  </span>
                  <span className="ow-metric-value">{meta.accessoryName} ({meta.accessoryConnected ? 'connected' : 'disconnected'})</span>
                </div>
              </div>
            )}
            {/* A Point does not stream, so latency and detection rate are
                absent by design here, not rendered at all - showing them
                empty would read as a fault (B5's explicit instruction). */}
          </>
        ) : (
          <div className="ow-metric-row">
            <div className="ow-metric-label-row">
              <span className="ow-metric-label">
                Stream status
                <InfoTooltip text="Whether the camera's video stream is reaching the recognition pipeline." />
              </span>
              <span className={`ow-badge ${appStale ? 'unknown' : (STATUS_CLASS[camera.status] || 'unknown')}`}>
                {appStale ? 'Unknown' : camera.status}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Prompt 2j-fix, Task E: lead with activity. Producing tracks is
          the single most useful per-camera signal we have - a camera
          CONNECTED and streaming but absent from the app tier's
          cameras-with-tracks list is producing nothing, the "pointed at
          a wall, out of focus, or misconfigured region" case that status
          alone calls healthy. Detection rate and per-camera latency are
          gone from this page entirely: both underlying Prometheus
          metrics turned out to be pipeline-wide counters carrying a
          stream label as an inventory tag, not a per-camera measurement
          - see docs/onwatch/README.md's metric audit section. */}
      {!camera.isPoint && metrics && (
        <div className="ow-node-card">
          <div className="ow-node-header">
            <span className="ow-node-name">Activity</span>
          </div>
          {metrics.hasRecentTracks === false ? (
            <div className="ow-activity-alert">
              <span className="ow-metric-label">
                Producing tracks: No
                <InfoTooltip text="Whether this camera has generated any detections in the last 24 hours. A connected camera producing nothing may be obstructed, out of focus, or have a misconfigured region of interest." />
              </span>
              {camera.status === 'CONNECTED' && !appStale && (
                <div style={{ marginTop: 4 }}>
                  Connected but producing no tracks - possibly out of focus, pointed away, or a misconfigured region of interest.
                </div>
              )}
            </div>
          ) : metrics.hasRecentTracks === true ? (
            <div className="ow-activity-ok">
              Producing tracks: Yes, in the last 24 hours
              <InfoTooltip text="Whether this camera has generated any detections in the last 24 hours. Shown quietly when healthy - only the No state is worth attention." />
            </div>
          ) : (
            <div className="ow-activity-ok">Producing tracks: not yet measured this cycle</div>
          )}
          <div className="ow-metric-row">
            <div className="ow-metric-label-row">
              <span className="ow-metric-label">
                Assigned cv-engine
                <InfoTooltip text="Which recognition engine instance is processing this camera. Useful when stream assignments are unbalanced." />
              </span>
              <span className="ow-metric-value">
                {shortenServiceAddress(metrics.pipeAssignment) || '—'}
                {metrics.pipeAssignment && <InfoTooltip text={`Full service address: ${metrics.pipeAssignment}`} />}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="ow-node-card">
        <div className="ow-node-header">
          <span className="ow-node-name">
            Unacknowledged recognition alerts
            <InfoTooltip text="People detected and not yet cleared by an operator - a face-recognition review queue, not a system fault count. Does not indicate whether these were matched to an enrolled subject or unrecognized; that breakdown is only available instance-wide, on the health page." />
          </span>
        </div>
        <div className="ow-recognition-queue-value">{unackAlertCount ?? '—'}</div>
        {/* A large and growing queue may itself be worth surfacing as an
            operational observation later (nobody clearing it is a real
            signal), but that is a workflow concern, not a system health
            alert - deliberately out of scope for this phase. */}
        <div className="ow-recognition-queue-note">
          Awaiting operator review, not a system error. Is anyone reviewing these, not whether they were recognized.
        </div>
        {formatUnackContext(unackAlertCount, unackAlertStats) && (
          <div className="ow-recognition-queue-context">{formatUnackContext(unackAlertCount, unackAlertStats)}</div>
        )}
      </div>

      <div className="ow-node-card">
        <div className="ow-node-header">
          <span className="ow-node-name">
            Disconnect history
            <InfoTooltip text="Uptime is measured only across time we were actually observing. Coverage tells you how much of the window that was. Frequent disconnects usually indicate a network or camera issue rather than a server problem." />
          </span>
        </div>
        <UptimeCardsSection
          uptimeByWindow={disconnectHistory.uptime}
          instanceRollupFirstSeenAt={instanceRollupFirstSeenAt}
          instanceUptimeBaseline={disconnectHistory.instanceUptimeBaseline}
        />
        {disconnectHistory.events.length === 0 ? (
          <div className="ow-form-help" style={{ marginTop: 10 }}>
            No transitions recorded yet. This history builds from here forward; a stable camera writes nothing for weeks, by design.
          </div>
        ) : (
          <div className="ow-history-timeline" style={{ marginTop: 10 }}>
            {disconnectHistory.events.map((e, i) => (
              <div key={i} className="ow-history-timeline-row">
                <span className={`ow-dot ${STATUS_CLASS[e.toStatus] || 'unknown'}`} />
                <span>{e.fromStatus || 'unknown'} → {e.toStatus}</span>
                <span className="ow-form-help">{new Date(e.occurredAt).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
